import React, { createContext, useContext, useState, useEffect } from 'react';
import { SCREENING_SECTIONS, THERAPIST_EVALUATION_SECTIONS } from '../lib/screeningQuestions';

const DataContext = createContext(null);

const initialProfiles = [
    { id: 1, name: 'Alex Johnson', age: 7, diagnosis: 'Autism Spectrum Disorder', supportLevel: 'Level 2', status: 'Active Evaluation', assignedTherapistId: 1 },
    { id: 2, name: 'Mia Wong', age: 5, diagnosis: 'ADHD', supportLevel: 'Level 1', status: 'Ongoing Intervention', assignedTherapistId: 1 },
    { id: 3, name: 'Leo Smith', age: 8, diagnosis: 'Dyslexia', supportLevel: 'Level 1', status: 'Discharged', assignedTherapistId: 1 },
    { id: 4, name: 'Noah Brown', age: 6, diagnosis: 'Developmental Delay', supportLevel: 'Level 2', status: 'Active Evaluation', assignedTherapistId: 99 },
    { id: 5, name: 'Emma Wilson', age: 9, diagnosis: 'Speech Sound Disorder', supportLevel: 'Level 1', status: 'Ongoing Intervention', assignedTherapistId: 99 },
];

const initialGoals = [
    {
        id: 1, profileId: 1, domain: 'Communication', title: 'Expressive Language using AAC',
        description: 'Alex will independently request preferred items using a 2-icon sequence.',
        status: 'In Progress', progress: 60, targetDate: 'Dec 2026', strategies: ['Model device usage']
    }
];

export const CHILD_SCREENING_TEMPLATE = {
    id: 'cs-final',
    title: 'Child Developmental Screening',
    duration: '~20 mins',
    domains: 8,
    sections: SCREENING_SECTIONS
};

export const THERAPIST_EVALUATION_TEMPLATE = {
    id: 'te-final',
    title: 'Therapist Clinical Evaluation',
    duration: '~30 mins',
    domains: 7,
    sections: THERAPIST_EVALUATION_SECTIONS
};

const DEFAULT_QUESTIONNAIRES = [
    {
        id: 'p1',
        title: 'Quarterly Self-Evaluation Check',
        duration: '~15 mins',
        domains: 4,
        questions: [
            "I am able to prepare basic meals for myself independently.",
            "I initiate social conversations with friends or peers often.",
            "I can organize my daily schedule without reminders.",
            "I feel confident using public transportation or navigating familiar routes.",
            "I handle unexpected changes in routine calmly."
        ]
    },
    {
        id: 'p2',
        title: 'Weekly Behavioral & Mood Tracker',
        duration: '~5 mins',
        domains: 2,
        questions: [
            "I managed my frustration without yelling or physical outbursts.",
            "I completed my given tasks and chores on time.",
            "I slept well and woke up rested.",
            "I participated actively in group or family activities."
        ]
    },
    {
        id: 'p3',
        title: 'Child Developmental Screening',
        duration: '~20 mins',
        domains: 8,
        questions: CHILD_SCREENING_TEMPLATE.sections.flatMap(s => s.questions.map(q => q.label))
    }
];

export const ADMIN_EVALUATIONS = [
    { id: 'a4', title: 'Child Developmental Screening', domains: 8, duration: '~20 mins', type: 'Screening', template: CHILD_SCREENING_TEMPLATE },
    { id: 't1', title: 'Therapist Clinical Evaluation', domains: 7, duration: '~30 mins', type: 'Baseline', template: THERAPIST_EVALUATION_TEMPLATE },
];

const DEFAULT_USERS = [
    { id: 1, name: 'Dr. Sarah Smith', role: 'Therapist', email: 'sarah@p2g.edu', status: 'Active', added: '2026-01-10' },
];

export function DataProvider({ children }) {
    // Persist profiles
    const [profiles, setProfiles] = useState(() => {
        try {
            const saved = localStorage.getItem('p2g_profiles');
            return saved ? JSON.parse(saved) : initialProfiles;
        } catch { return initialProfiles; }
    });
    const [goals, setGoals] = useState(initialGoals);

    // Persist stateful questionnaires
    const [patientQuestionnaires, setPatientQuestionnaires] = useState(() => {
        try {
            const saved = localStorage.getItem('p2g_questionnaires');
            if (saved) {
                const parsed = JSON.parse(saved);
                // Force sync the core protocol to prevent cache bugs
                const p3Index = parsed.findIndex(p => p.id === 'p3');
                const targetP3 = DEFAULT_QUESTIONNAIRES.find(p => p.id === 'p3');
                if (p3Index !== -1 && targetP3) {
                    parsed[p3Index] = targetP3;
                } else if (targetP3) {
                    parsed.push(targetP3);
                }
                return parsed;
            }
            return DEFAULT_QUESTIONNAIRES;
        } catch { return DEFAULT_QUESTIONNAIRES; }
    });

    // Persist submitted assessments
    const [assessments, setAssessments] = useState(() => {
        try { return JSON.parse(localStorage.getItem('p2g_assessments') || '[]'); } catch { return []; }
    });

    const [assignedAssessments, setAssignedAssessments] = useState(() => {
        try { return JSON.parse(localStorage.getItem('p2g_assigned') || '[]'); } catch { return []; }
    });

    // Registered accounts (from login/register) — persisted
    const [registeredAccounts, setRegisteredAccounts] = useState(() => {
        try {
            const saved = localStorage.getItem('p2g_accounts');
            return saved ? JSON.parse(saved) : DEFAULT_USERS;
        } catch { return DEFAULT_USERS; }
    });

    // Persist admin evaluations
    const [adminEvaluations, setAdminEvaluations] = useState(() => {
        try {
            const saved = localStorage.getItem('p2g_admin_evals');
            return saved ? JSON.parse(saved) : ADMIN_EVALUATIONS;
        } catch { return ADMIN_EVALUATIONS; }
    });

    // Persist effects
    useEffect(() => {
        localStorage.setItem('p2g_profiles', JSON.stringify(profiles));
    }, [profiles]);

    useEffect(() => {
        localStorage.setItem('p2g_questionnaires', JSON.stringify(patientQuestionnaires));
    }, [patientQuestionnaires]);

    useEffect(() => {
        localStorage.setItem('p2g_assessments', JSON.stringify(assessments));
    }, [assessments]);

    useEffect(() => {
        localStorage.setItem('p2g_assigned', JSON.stringify(assignedAssessments));
    }, [assignedAssessments]);

    useEffect(() => {
        localStorage.setItem('p2g_accounts', JSON.stringify(registeredAccounts));
    }, [registeredAccounts]);

    useEffect(() => {
        localStorage.setItem('p2g_admin_evals', JSON.stringify(adminEvaluations));
    }, [adminEvaluations]);

    // Sync real patient profiles from MySQL backend
    useEffect(() => {
        const syncRealUsers = async () => {
            try {
                const apiBase = process.env.REACT_APP_API_URL || "https://doctor-appointment-1-yf2j.onrender.com";
                const res = await fetch(`${apiBase}/api/users`);
                if (res.ok) {
                    const usersList = await res.json();
                    if (Array.isArray(usersList) && usersList.length > 0) {
                        setProfiles(prev => {
                            const map = new Map();
                            usersList.forEach(u => {
                                map.set(u.id.toString(), {
                                    id: u.id,
                                    name: u.name,
                                    email: u.email,
                                    age: u.dob ? Math.max(1, Math.floor((new Date() - new Date(u.dob)) / (365.25 * 24 * 60 * 60 * 1000))) : 25,
                                    diagnosis: 'Clinical Screening Profile',
                                    supportLevel: 'Active',
                                    status: 'Active Evaluation',
                                    assignedTherapistId: 1
                                });
                            });
                            // Keep any customized non-dummy profiles
                            prev.forEach(p => {
                                if (!map.has(p.id?.toString()) && !initialProfiles.some(ip => ip.id === p.id)) {
                                    map.set(p.id.toString(), p);
                                }
                            });
                            return Array.from(map.values());
                        });
                    }
                }
            } catch (err) {
                // Non-critical fallback
            }
        };
        syncRealUsers();
    }, []);

    const addProfile = (profile) => {
        const newId = profile.id || Date.now();
        const newProfile = {
            ...profile,
            id: newId,
            assignedTherapistId: profile.assignedTherapistId || 1,
            status: profile.status || 'Active Evaluation'
        };
        setProfiles(prev => {
            const exists = prev.some(p => p.id?.toString() === newId?.toString());
            if (exists) return prev.map(p => p.id?.toString() === newId?.toString() ? newProfile : p);
            return [...prev, newProfile];
        });
        return newProfile;
    };

    const updateProfile = (updated) => setProfiles(prev => prev.map(p => p.id?.toString() === updated.id?.toString() ? { ...p, ...updated } : p));

    const assignAssessment = (assignment) =>
        setAssignedAssessments(prev => [...prev, {
            ...assignment,
            id: Date.now(),
            assignedDate: new Date().toISOString(),
            frequency: assignment.frequency || 'Monthly'
        }]);

    const submitAssessment = (data) => {
        setAssessments(prev => [...prev, { ...data, id: Date.now(), date: new Date().toISOString() }]);
        // Remove from assigned tasks if it matches
        setAssignedAssessments(prev => prev.filter(a => {
            const normalizedAssignedTitle = a.assessmentTitle?.toLowerCase().replace('(final)', '').trim();
            const normalizedSubmitTitle = data.type?.toLowerCase().replace('(final)', '').trim();
            const matchTitle = normalizedAssignedTitle === normalizedSubmitTitle;
            const matchPatient = a.patientId?.toString() === data.patientId?.toString() || a.patientName === data.patientName;
            return !(matchTitle && matchPatient);
        }));
    };


    const addGoal = (goal) => setGoals(prev => [...prev, { ...goal, id: Date.now(), progress: 0, status: 'Not Started' }]);
    const updateGoalProgress = (id, progress) => {
        setGoals(prev => prev.map(g => g.id === id ? { ...g, progress, status: progress === 100 ? 'Mastered' : 'In Progress' } : g));
    };

    const addPatientQuestionnaire = (q) => {
        const newId = `pq-${Date.now()}`;
        const newQ = {
            ...q,
            id: newId,
            domains: q.questions.length,
        };
        setPatientQuestionnaires(prev => [...prev, newQ]);
        return newQ;
    };

    const registerAccount = (account) => {
        setRegisteredAccounts(prev => {
            const exists = prev.some(u => u.email === account.email);
            if (exists) return prev.map(u => u.email === account.email ? { ...u, ...account } : u);
            return [...prev, {
                id: account.id || Date.now(),
                name: account.name,
                email: account.email,
                role: account.role === 'Patient / Self-Advocate' ? 'Patient' : account.role,
                patientId: account.patientId,
                patientName: account.patientName,
                status: 'Active',
                added: new Date().toISOString().split('T')[0]
            }];
        });
    };

    const updateAccountStatus = (email, status) => {
        setRegisteredAccounts(prev => prev.map(u => u.email === email ? { ...u, status } : u));
    };

    // Legacy addUser kept for Admin invite form compatibility
    const addUser = (user) => {
        const newUser = { ...user, id: Date.now(), added: new Date().toISOString().split('T')[0], status: 'Active' };
        setRegisteredAccounts(prev => {
            const exists = prev.some(u => u.email === user.email);
            if (exists) return prev;
            return [...prev, newUser];
        });
    };

    const addAdminEvaluation = (evaluation) => {
        const newId = `admin-${Date.now()}`;
        const newEval = {
            ...evaluation,
            id: newId,
        };
        setAdminEvaluations(prev => [...prev, newEval]);
        return newEval;
    };

    const updateAdminEvaluation = (updatedEval) => {
        setAdminEvaluations(prev => prev.map(e => e.id === updatedEval.id ? updatedEval : e));
    };

    return (
        <DataContext.Provider value={{
            profiles, addProfile, updateProfile,
            goals, setGoals, addGoal, updateGoalProgress,
            assessments, submitAssessment,
            assignedAssessments, assignAssessment,
            registeredAccounts, registerAccount, updateAccountStatus,
            users: registeredAccounts,  // alias so existing code still works
            addUser,
            patientQuestionnaires, addPatientQuestionnaire,
            adminEvaluations, addAdminEvaluation, updateAdminEvaluation,
        }}>
            {children}
        </DataContext.Provider>
    );
}

export const useData = () => useContext(DataContext);
